import React, { useState } from "react";
import { useMitcidco } from "./MitcidcoLayout";
import { PageHero, Section } from "./_shared";
import { Camera, X, ChevronLeft, ChevronRight } from "lucide-react";

export default function Gallery() {
  const data = useMitcidco();
  const photos = data?.gallery || [];
  const [idx, setIdx] = useState(-1);
  const [filter, setFilter] = useState("");

  const types = Array.from(new Set(photos.map((p) => p.gallery_type).filter(Boolean)));
  const visible = filter ? photos.filter((p) => p.gallery_type === filter) : photos;

  return (
    <div data-testid="mitcidco-gallery">
      <PageHero
        eyebrow="Campus Gallery"
        title="Life at Marathwada ITI CIDCO"
        sub="Snapshots from workshops, events, community drives and campus celebrations."
      />
      <Section>
        {types.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-8">
            <button
              type="button"
              onClick={() => setFilter("")}
              className={`text-[11px] uppercase tracking-wider font-bold px-4 py-2 transition-colors ${
                filter === "" ? "bg-[#7C2D12] text-white" : "bg-white text-slate-700 border border-amber-100 hover:bg-[#FDF6E3]"
              }`}
              data-testid="mitcidco-gallery-filter-all"
            >
              All ({photos.length})
            </button>
            {types.map((t) => {
              const count = photos.filter((p) => p.gallery_type === t).length;
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => setFilter(t)}
                  className={`text-[11px] uppercase tracking-wider font-bold px-4 py-2 transition-colors ${
                    filter === t ? "bg-[#D4A017] text-[#5B1A0A]" : "bg-white text-slate-700 border border-amber-100 hover:bg-[#FDF6E3]"
                  }`}
                  data-testid={`mitcidco-gallery-filter-${t.replace(/\s+/g, "-")}`}
                >
                  {t} ({count})
                </button>
              );
            })}
          </div>
        )}

        {visible.length === 0 ? (
          <div className="bg-white border-2 border-dashed border-amber-100 p-16 text-center" data-testid="mitcidco-gallery-empty">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[#D4A017]/15 text-[#B45309]">
              <Camera size={28} />
            </div>
            <div className="font-serif text-xl text-[#7C2D12] mt-5">Gallery coming soon</div>
            <p className="text-sm text-slate-500 mt-2 max-w-md mx-auto">Institute photographs are being curated. Please check back shortly.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {visible.map((p, i) => (
              <button
                key={p.id}
                type="button"
                onClick={() => setIdx(i)}
                data-testid={`mitcidco-gallery-photo-${p.id}`}
                className="group aspect-square overflow-hidden bg-slate-100 relative"
              >
                <img src={p.image_url} alt={p.caption || ""} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" loading="lazy" />
                {p.caption && (
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 to-transparent p-2.5 text-[11px] text-white text-left leading-snug opacity-0 group-hover:opacity-100 transition-opacity">
                    {p.caption}
                  </div>
                )}
                {p.gallery_type && (
                  <div className="absolute top-2 left-2 bg-white/95 text-[9px] tracking-[0.15em] uppercase font-bold text-[#7C2D12] px-2 py-0.5">
                    {p.gallery_type}
                  </div>
                )}
              </button>
            ))}
          </div>
        )}
      </Section>

      {idx >= 0 && (
        <Lightbox photos={visible} idx={idx} onIdx={setIdx} onClose={() => setIdx(-1)} />
      )}
    </div>
  );
}

function Lightbox({ photos, idx, onIdx, onClose }) {
  const cur = photos[idx];
  if (!cur) return null;
  const prev = () => onIdx((idx - 1 + photos.length) % photos.length);
  const next = () => onIdx((idx + 1) % photos.length);
  return (
    <div className="fixed inset-0 bg-black/90 z-[100] flex items-center justify-center p-4" onClick={onClose} data-testid="mitcidco-gallery-lightbox">
      <button type="button" onClick={onClose} className="absolute top-4 right-4 text-white p-2 hover:bg-white/10" aria-label="Close"><X /></button>
      {photos.length > 1 && (
        <>
          <button type="button" onClick={(e) => { e.stopPropagation(); prev(); }} className="absolute left-4 text-white p-2 hover:bg-white/10" aria-label="Prev"><ChevronLeft /></button>
          <button type="button" onClick={(e) => { e.stopPropagation(); next(); }} className="absolute right-4 text-white p-2 hover:bg-white/10" aria-label="Next"><ChevronRight /></button>
        </>
      )}
      <div className="max-w-5xl max-h-[85vh] flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
        <img src={cur.image_url} alt={cur.caption || ""} className="max-w-full max-h-[80vh] object-contain" />
        {cur.caption && <div className="mt-4 text-white text-sm text-center max-w-2xl">{cur.caption}</div>}
      </div>
    </div>
  );
}
